import React, { FC } from 'react';
import styled from 'styled-components';
import colors from '../../utils/colors';
import { Row } from './layoutUtils';
import { MediumText } from '../../utils/typography';

type Props = {
  onChange: () => void;
  active: boolean;
  label?: string;
  iconPath?: string;
  className?: string;
  reverse?: boolean;
  disabled?: boolean;
  alignBaseline?: boolean;
  customLabel?: any;
  /** 'md' is the settings-page switch: big enough to see and hit, white knob, grey when off. */
  size?: 'sm' | 'md';
};

const Toggle: FC<Props> = ({
  onChange,
  active,
  label,
  iconPath,
  className,
  reverse,
  disabled,
  alignBaseline = false,
  customLabel,
  size = 'sm'
}) => (
  <Container
    className={`${className} ${disabled ? 'disabled' : ''}`}
    onClick={() => !disabled && onChange()}
    baseline={alignBaseline}
  >
    {iconPath && <Icon path={iconPath} />}
    {reverse ? (
      <>
        <ToggleWrapper>
          <Handle active={active} size={size} />
          <Area active={active} size={size} />
        </ToggleWrapper>
        {customLabel || <Label strong={active}>{label}</Label>}
      </>
    ) : (
      <>
        {customLabel || <Label strong={active}>{label}</Label>}
        <ToggleWrapper>
          <Handle active={active} size={size} />
          <Area active={active} size={size} />
        </ToggleWrapper>
      </>
    )}
  </Container>
);

export default Toggle;

const Container = styled(Row)<{ baseline?: boolean }>`
  cursor: pointer;
  ${props => props.baseline && `align-items: baseline;`};

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Icon = styled.div<{ path: string }>`
  width: 14px;
  height: 14px;
  background: url("${props => props.path}") no-repeat center;
  background-size: cover;
`;

const Label = styled(MediumText)`
  font-size: 12px;
  font-weight: bold;
  transition: color 0.2s ease;
  margin: 0 6px;
  white-space: pre;
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  position: relative;
`;

const Handle = styled.div<{ active: boolean; size: 'sm' | 'md' }>`
  border-radius: 50%;
  position: absolute;
  top: 2px;
  transition: all 0.2s ease;
  ${props =>
    props.size === 'md'
      ? `width: 14px;
         height: 14px;
         background-color: ${colors.white};
         box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
         right: ${props.active ? 2 : 16}px;`
      : `width: 6px;
         height: 6px;
         background-color: ${props.theme.background};
         right: ${props.active ? 2 : 12}px;`}
`;

const Area = styled.div<{ active: boolean; size: 'sm' | 'md' }>`
  transition: background-color 0.2s ease;
  ${props =>
    props.size === 'md'
      ? `width: 32px;
         height: 18px;
         border-radius: 9px;
         background-color: ${props.active ? colors.primaryPurple : `${colors.gray}80`};`
      : `width: 20px;
         height: 10px;
         border-radius: 10px;
         background-color: ${props.active ? colors.primaryPurple : props.theme.textColor};`}
`;
